'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AuthRole = 'admin' | 'issuer' | 'investor';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: AuthRole;
  orgName: string;
  /** Ready to swap: pass this token as Bearer to Laravel /api/v1/auth endpoints */
  token: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (data: SignupData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: AuthRole;
  orgName?: string;
}

// ─── Mock credential store (swap with Laravel /api/v1/auth/login) ─────────────

const MOCK_USERS: (AuthUser & { password: string })[] = [
  {
    id: 'usr-admin-001',
    email: 'admin@bitsukuk.com',
    password: 'admin123',
    firstName: 'Amirul',
    lastName: 'Haziq',
    role: 'admin',
    orgName: 'BitSukuk™ Platform',
    token: 'mock-jwt-admin-token',
  },
  {
    id: 'usr-issuer-001',
    email: 'issuer@bitsukuk.com',
    password: 'issuer123',
    firstName: 'Khalid',
    lastName: 'Al-Rashidi',
    role: 'issuer',
    orgName: 'Al-Noor Capital',
    token: 'mock-jwt-issuer-token',
  },
  {
    id: 'usr-investor-001',
    email: 'investor@bitsukuk.com',
    password: 'investor123',
    firstName: 'Siti',
    lastName: 'Aminah',
    role: 'investor',
    orgName: 'Amanah Wealth Management',
    token: 'mock-jwt-investor-token',
  },
];

const SESSION_KEY = 'bitsukuk_session';

// ─── Role → default dashboard route ──────────────────────────────────────────

export const ROLE_DASHBOARDS: Record<AuthRole, string> = {
  admin: '/admin-panel',
  issuer: '/issuer-dashboard',
  investor: '/investor-dashboard',
};

// ─── Context ──────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Rehydrate session from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (raw) {
        const parsed: AuthUser = JSON.parse(raw);
        setUser(parsed);
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const persistUser = (u: AuthUser) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(u));
    setUser(u);
  };

  /**
   * login — simulates POST /api/v1/auth/login
   * To connect Laravel: replace with fetch('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) })
   * then call persistUser(response.user) and store response.token in user.token
   */
  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 900));

    const found = MOCK_USERS.find(
      u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );

    setIsLoading(false);

    if (!found) {
      return { success: false, error: 'Invalid email address or password.' };
    }

    const { password: _pw, ...authUser } = found;
    persistUser(authUser);
    return { success: true };
  }, []);

  /**
   * signup — simulates POST /api/v1/auth/register
   * To connect Laravel: replace with fetch('/api/v1/auth/register', { method: 'POST', body: JSON.stringify(data) })
   */
  const signup = useCallback(async (data: SignupData) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1000));

    const exists = MOCK_USERS.find(u => u.email.toLowerCase() === data.email.toLowerCase());
    setIsLoading(false);

    if (exists) {
      return { success: false, error: 'An account with this email already exists.' };
    }

    const newUser: AuthUser = {
      id: `usr-${data.role}-${Date.now()}`,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      orgName: data.orgName ?? `${data.firstName}'s Organisation`,
      token: `mock-jwt-${data.role}-new-token`,
    };

    persistUser(newUser);
    return { success: true };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider data-component-id="src/context/AuthContext.tsx:170:4" data-component-path="src/context/AuthContext.tsx" data-component-line="170" data-component-end-line="172" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%7D" value={{ user, isLoading, isAuthenticated: !!user, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
